import React from "react";
import BaseModel from "../models/BaseModel";
import type Cabinet from "../models/Cabinet";
import TreeViewModel, { DragMode, DropResult } from "../models/TreeviewModel";
import Logger from "../Logger";
import DeviceTemplate from "../models/DeviceTemplate";
import { ChannelType } from "../models/ChannelType";
import TreeMenuIcon from "../components/NavigationTree2/TreeMenuIcon";
import ELCB from "../models/ELCB";
import Fuse from "../models/Fuse";
import TerminalStrip from "../models/TerminalStrip";
import { itx } from "../translationItx/itx";
import { EventKey } from "../eventLogger/EventKeys";
import { isDevelopmentEnvironment } from "../estada-config";

const logger = new Logger();

export default class CabinetTreeViewModel extends TreeViewModel {
  get typedObj(): Cabinet {
    return this.obj as Cabinet;
  }

  get isFolder(): boolean {
    return true;
  }

  getTagNeeded(): boolean {
    return true;
  }

  get childCollection(): Array<BaseModel> | undefined {
    let children = new Array<BaseModel>();
    children.push(...this.typedObj.actors.sortBy("tagOrName"));

    children.push(
      ...this.typedObj.elcbs.sortBy("tagOrName"),
      ...this.typedObj.terminalStrips.sortBy("tagOrName"),
      ...this.typedObj.fi_ls_s.sortBy("tagOrName"),
    );

    return children;
  }

  handleOpen(transitOpen: boolean = false): boolean {
    return this.openDevice(this.typedObj.id, transitOpen);
  }

  ////////////////////////////////////////////////////////////////////////////////////////

  ////////////////////////////////////////////////////////////////////////////////////////

  handleAdd() {
    this.handleAddActor("Actor"); // no itx
  }

  handleAddActor(
    type: "Actor" | "Supply", // no itx
    openPane = true,
  ) {
    // state.dialogManager.openDialog(<ActorDialog parent={this.typedObj} />);
    const newActor = this.typedObj.createActor({ channelType: ChannelType.Generic }, this.typedObj);
    //const newChannel = newActor.createNewChannel();
    newActor.numberChannelsFromUser = 0;
    if (
      type == "Supply" // no itx
    ) {
      newActor.channelType = ChannelType.suply230V;
      newActor.name = itx("channelType." + newActor.channelType);
    }

    this.typedObj.actors.add(newActor);
    state.eventLogger.addEvent(EventKey.ObjectCreated, {
      objectType: "Actor",
      obj: newActor.id + "_" + newActor.tag + "_" + newActor.name,
    });
    if (openPane) state.activePanes.openPane(newActor);
    return newActor;
  }

  handleAddELCB(withFuse = true): ELCB | undefined {
    // state.dialogManager.openDialog(<ActorDialog parent={this.typedObj} />);
    const newELCB = new ELCB(
      { tag: `${state.settings.devicePrefix.elcb}${this.typedObj.elcbs.size + 1}` },
      this.typedObj,
    );
    this.typedObj.elcbs.add(newELCB);
    newELCB.tag = state.nameAndTagManager.generateTag(newELCB);

    if (withFuse) {
      const newFuse = new Fuse({}, newELCB);
      newFuse.tag = state.nameAndTagManager.generateTag(newFuse);
      newELCB.fuses.add(newFuse);
    }
    state.eventLogger.addEvent(EventKey.ObjectCreated, {
      objectType: "ELCB",
      obj: newELCB.id + "_" + newELCB.tag + "_" + newELCB.name,
    });
    state.selectionManager.selectedObject = newELCB;
    return newELCB;
  }

  handleAdd_Fi_Ls(openPane = true) {
    // state.dialogManager.openDialog(<ActorDialog parent={this.typedObj} />);

    const tag = `${state.settings.devicePrefix.fuse}${this.typedObj.fi_ls_s.size + 1}`;

    const newFuse = new Fuse({ tag: tag, name: itx("components.FILS", "RCBO"), is_FI_LS: true }, this.typedObj); // no itx

    this.typedObj.fi_ls_s.add(newFuse);
    newFuse.tag = state.nameAndTagManager.generateTag(newFuse);

    state.eventLogger.addEvent(EventKey.ObjectCreated, {
      objectType: "FI",
      obj: newFuse.id + "_" + newFuse.tag + "_" + newFuse.name,
    });
    state.selectionManager.selectedObject = newFuse;
    if (openPane) state.activePanes.openPane(newFuse);
    return newFuse;
  }

  handleAddTerminalStrip(openPane = true): TerminalStrip | undefined {
    const newTerminalStrip = new TerminalStrip({ tag: `X${this.typedObj.terminalStrips.size + 1}` }, this.typedObj);
    this.typedObj.terminalStrips.add(newTerminalStrip);
    newTerminalStrip.tag = state.nameAndTagManager.generateTag(newTerminalStrip);
    state.eventLogger.addEvent(EventKey.ObjectCreated, {
      objectType: "TerminalStrip",
      obj: newTerminalStrip.id + "_" + newTerminalStrip.tag + "_" + newTerminalStrip.name,
    });
    state.selectionManager.selectedObject = newTerminalStrip;
    if (openPane) state.activePanes.openPane(newTerminalStrip);
    return newTerminalStrip;
  }

  buildAddChildMenuItems(): any {
    return {
      title: state.translate("treeViewModel.connect", "Hinzufügen"),
      items: [
        {
          id: `actor`,
          image: (
            <i
              draggable="false" // no itx
              style={{ width: "auto", height: "25px", objectFit: "contain" }}
              className={"las la-rotate-90 la-server"} // no itx
            />
          ),
          label: "Aktor", // no itx
          onClick: () => {
            this.handleAddActor("Actor"); // no itx
          }, // no itx
        },
        {
          id: `supply`,
          image: (
            <i
              draggable="false" // no itx
              style={{ width: "auto", height: "25px", objectFit: "contain" }}
              className={"las la-rotate-90 la-server"} // no itx
            />
          ),
          label: "Spannungs-Versorgung", // no itx
          onClick: () => {
            this.handleAddActor("Supply"); // no itx
          }, // no itx
        },
        {
          id: `Fi1`,
          image: (
            <img
              draggable="false" // no itx
              style={{ width: "auto", height: "20px", objectFit: "contain", opacity: "0.8" }}
              src={state.hostBaseUrl + "images/fuse/elcb.png"}
            />
          ),
          label: "RCBO", // no itx
          onClick: () => {
            this.handleAddELCB();
          },
        },
        {
          id: `FiLs1`,
          image: (
            <img
              draggable="false" // no itx
              style={{ width: "auto", height: "20px", objectFit: "contain", opacity: "0.8" }}
              src={state.hostBaseUrl + "images/fuse/fuse.png"}
            />
          ),
          label: "LS", // no itx
          onClick: () => {
            this.handleAdd_Fi_Ls();
          },
        },
        {
          id: `TS`,
          image: (
            <img
              draggable="false" // no itx
              style={{ width: "auto", height: "20px", objectFit: "contain", opacity: "0.8" }}
              src={state.hostBaseUrl + "images/terminal/terminalStrip.png"}
            />
          ),
          label: itx("components.TerminalStrip", "Klemmleiste"),
          onClick: () => {
            this.handleAddTerminalStrip();
          },
        },
      ],
    };
  }

  handleAddWithEvent(event: any) {
    this.handleAddChild(event);
  }

  handleAddChild(event: any) {
    state.contextMenuManager.show1(event, this.buildAddChildMenuItems(), []);
  }

  getButtonsAsContextMenuItems(): any[] {
    return this.buttonsAsContextMenuItems().map((item) => {
      if (item.id === "btn_plus-circle") {
        return { ...item, menu: this.buildAddChildMenuItems(), onClick: undefined };
      }
      return item;
    });
  }

  inlineButton_Add() {
    return (
      <TreeMenuIcon
        onClick={(ev: any) => {
          this.handleAddChild(ev);
        }}
        iconName="plus-circle"
        tooltipKey="inlineButton.addActorOrELCB" // no itx
        objName={this.obj.className + this.obj.fullTag}
        key={this.obj.className + this.obj.fullTag + "add"} // no itx
      />
    );
  }

  ////////////////////////////////////////////////////////////////////////////////////////

  allowDrop(event: any): boolean {
    return true;
  }

  allowDrag(): boolean {
    return false;
  }

  async handleDrop2(mode: DragMode, obj: BaseModel): Promise<DropResult> {
    switch (obj.className) {
      case "DeviceTemplate": {
        const deviceTemplate = obj as DeviceTemplate;
        if (deviceTemplate.isActor) {
          // create a new Actor
          const a = this.typedObj.autoCreateActor(ChannelType.Unknown);
          if (a == undefined) {
            return new DropResult(
              false,
              itx("floorCreateAskBack.onlyActorsInCabinet", "In Schaltschränken sind nur Aktoren zulässig"),
            );
          }
          a.template = deviceTemplate;
          a.name = deviceTemplate.name;
          state.selectionManager.selectedObject = a;
          a.updateAllFromKnxMetadata();
          return new DropResult(true);
        } else {
          return new DropResult(
            false,
            itx("floorCreateAskBack.onlyActorsInCabinet", "In Schaltschränken sind nur Aktoren zulässig"),
          );
        }
        break;
      }
      default:
        return super.handleDrop2(mode, obj);
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////

  getButtons(): JSX.Element[] | undefined {
    const buttons: Array<any> = [];
    if (isDevelopmentEnvironment()) {
      buttons.push(super.inlineButton_Open("inlineButton.editCabinet"));
    }
    buttons.push(this.inlineButton_Add());

    return buttons;
  }

  ////////////////////////////////////////////////////////////////////////////////////////

  canBePastedInteractive(copiedObjClassName: string): boolean {
    if (copiedObjClassName == "Cabinet" || copiedObjClassName == "Actor") {
      return true;
    }
    return false;
  }

  async getDisabledContextMenuItems(): Promise<Array<string>> {
    const copiedObjClassName = state.clipboard.getClassName();

    if (copiedObjClassName == "Cabinet" || copiedObjClassName == "Actor") {
      return new Array<string>();
    }

    return ["paste"]; // no itx
  }

  get contextMenuItems(): any {
    var subLabelPaste = undefined;

    try {
      subLabelPaste = state.contextMenuManager.clipboardData.items[0].name;
    } catch (ex) {
      logger.error(ex);
    }

    return {
      items: [
        this.baseContextMenuItems_Copy(),
        this.baseContextMenuItems_Paste(subLabelPaste),
        this.baseContextMenuItems_Delete(),
      ],
    };
  }
}
